import { useState } from 'react';
import type { ProcessingStatus } from '@/shared/types';
import { Button, Modal, Spinner } from '@/shared/ui';
import { formatCredits } from '@/shared/utils/format';
import { formatEstimatedDuration } from '@/features/generation/utils/estimate';
import { generationPhase } from '@/features/generation/utils/generationPhase';
import { safeErrorMessage } from '@/shared/utils/errors';
import { logger } from '@/shared/utils/logger';

/**
 * The `awaiting_confirmation` dialog: the engine has planned the story and is
 * quoting a cost and an ETA before it renders anything billable.
 *
 * This used to exist twice — once in SubmissionPage with four states and a fully
 * inline-styled spinner, once in VideoPage with only the confirm step and its own
 * copy of the ETA formatting. Both drive the same backend transition, so they are
 * one component now.
 */

export type ConfirmGenerationModalProps = {
  open: boolean;
  status: ProcessingStatus | undefined;
  estimatedCost?: number;
  estimatedDurationSeconds?: number;
  errorDetail?: string;
  onConfirm: () => Promise<void>;
  onCancel: () => Promise<void>;
  /**
   * Dismisses the terminal steps. Omit where the caller unmounts the dialog
   * itself on the status change (VideoPage) rather than tracking a pending id.
   */
  onDismiss?: () => void;
};

export function ConfirmGenerationModal({
  open,
  status,
  estimatedCost,
  estimatedDurationSeconds,
  errorDetail,
  onConfirm,
  onCancel,
  onDismiss,
}: ConfirmGenerationModalProps) {
  // Both call sites had the identical try/catch/setError around confirm and
  // cancel, so the dialog owns it.
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'confirm' | 'cancel' | null>(null);

  const phase = generationPhase(status, estimatedCost);

  const run = async (which: 'confirm' | 'cancel', action: () => Promise<void>) => {
    setBusy(which);
    try {
      await action();
      setActionError(null);
    } catch (err) {
      logger.error(`Failed to ${which} video generation`, err);
      setActionError(safeErrorMessage(err, `Failed to ${which}. Please try again.`));
    } finally {
      setBusy(null);
    }
  };

  if (phase === 'preparing') {
    return (
      /* Cancel is the one control this step gets. It used to have none, on the
         reasoning that there is nothing yet to accept — but a quote that never
         arrives (an older job, or a failed estimate request) then left the user
         inside a modal with no close button, no Escape and a locked body, with
         no way off the page. Cancel is not the blind-accept risk that argument
         was about; it is the escape hatch, and it stops the job besides.
         Escape stays inert here as on the confirm step: ending a generation on
         a stray keypress is not a thing this dialog should do. */
      <Modal
        open={open}
        title="Processing request"
        size="sm"
        footer={
          <Button onClick={() => void run('cancel', onCancel)} loading={busy === 'cancel'}>
            Cancel
          </Button>
        }
      >
        <div className="confirm-gen__waiting">
          <Spinner size={32} label="Generating story plan" />
          <p>Generating story plan and calculating time and cost estimates…</p>
        </div>

        {actionError ? (
          <p role="alert" className="confirm-gen__error">
            {actionError}
          </p>
        ) : null}
      </Modal>
    );
  }

  if (phase === 'cancelled') {
    return (
      <Modal
        open={open}
        onClose={onDismiss}
        title="Generation cancelled"
        footer={
          onDismiss ? (
            <Button variant="primary" onClick={onDismiss}>
              Close
            </Button>
          ) : null
        }
      >
        <p>The video was not created.</p>
      </Modal>
    );
  }

  if (phase === 'failed') {
    return (
      <Modal
        open={open}
        onClose={onDismiss}
        title="Generation failed"
        footer={
          onDismiss ? (
            <Button variant="primary" onClick={onDismiss}>
              Close
            </Button>
          ) : null
        }
      >
        <p>{errorDetail || 'An unexpected error occurred while generating prompts.'}</p>
      </Modal>
    );
  }

  if (phase === 'confirmed') {
    return (
      <Modal
        open={open}
        onClose={onDismiss}
        title="Job confirmed"
        footer={
          onDismiss ? (
            <Button variant="primary" onClick={onDismiss}>
              OK
            </Button>
          ) : null
        }
      >
        <p>The job is now being processed in the background.</p>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="Confirm video generation"
      description="Your story prompts are ready. Confirm to start rendering."
      footer={
        <>
          <Button onClick={() => void run('cancel', onCancel)} loading={busy === 'cancel'}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void run('confirm', onConfirm)}
            loading={busy === 'confirm'}
          >
            Confirm &amp; Proceed
          </Button>
        </>
      }
    >
      <dl className="confirm-gen__estimate">
        <div className="confirm-gen__row">
          <dt>Estimated time</dt>
          <dd>{formatEstimatedDuration(estimatedDurationSeconds)}</dd>
        </div>
        <div className="confirm-gen__row">
          <dt>Estimated cost</dt>
          {/* Credits, not dollars — matches the balance and the activity feed. A
              "$" here read as a price while the value was pre-margin provider
              cost, which made the understatement invisible. */}
          <dd>
            {estimatedCost !== undefined ? `${formatCredits(estimatedCost)} credits` : 'Calculating…'}
          </dd>
        </div>
      </dl>

      {actionError ? (
        <p role="alert" className="confirm-gen__error">
          {actionError}
        </p>
      ) : null}
    </Modal>
  );
}
